import { useEffect, useRef, useState } from 'react';
import { C, POLICE_TITRE, POLICE_CORPS, DEGRADE_ACTION, MENTHE_PLEINE, TRAIT_COCHE } from '../../theme';
import { ChevronGauche, Coche } from '../../icones';
import { BoutonPrincipal, Chargement, Panne } from '../composants/base';
import { FormulaireCorrection, type PayloadCorrige } from '../composants/FormulaireCorrection';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import { ChoixCercle } from '../composants/ChoixCercle';
import { PhotoDeBoite } from '../composants/PhotoDeBoite';
import { jourEnToutesLettres } from '../format';
import type { Cercle, ElementBoite, Proposition, Question } from '../../api/types';

const ETIQUETTES: Record<Proposition['kind'], string> = {
  EVENT: 'ÉVÉNEMENT',
  TASK: 'TÂCHE',
  REMINDER: 'RAPPEL',
  CHECKLIST: 'CHECKLIST',
  // Pas une chose a FAIRE : une chose a SAVOIR. La famille doit voir ce qu'elle confie a
  // la memoire avant de le valider (etape B2).
  FACT: 'À RETENIR',
};

const NOMS_DES_JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/**
 * Le mot de la fin quand Google n'a pas repondu. Ne JAMAIS laisser croire a une reussite
 * complete : l'evenement est bien dans Colette, mais pas dans l'agenda Google.
 */
function suffixeGoogle(resultat: unknown): string {
  const echoue = (r: unknown): boolean =>
    typeof r === 'object' && r !== null && (r as { google?: string }).google === 'echec';
  const auMoinsUn = Array.isArray(resultat) ? resultat.some(echoue) : echoue(resultat);
  return auMoinsUn ? ' — mais Google Agenda n’a pas répondu, c’est noté dans Colette' : '';
}

/** « Chaque jour », « Chaque lundi », « Chaque lundi, mercredi, vendredi ». */
function libelleRecurrence(
  recurrence: { freq: 'DAILY' | 'WEEKLY'; days: number[] },
  premiereDate: string,
): string {
  if (recurrence.freq === 'DAILY') return 'Chaque jour';
  const jours =
    recurrence.days.length > 0
      ? recurrence.days
      : [new Date(`${premiereDate}T12:00:00Z`).getUTCDay()];
  return `Chaque ${jours.map((j) => NOMS_DES_JOURS[j]).join(', ')}`;
}

/** Titre, detail et puces d'une proposition, selon son genre. */
function decrire(proposition: Proposition): {
  titre: string;
  detail: string | null;
  puces: string[];
} {
  switch (proposition.kind) {
    case 'EVENT': {
      const p = proposition.payload;
      // Une habitude s'annonce comme telle : « Chaque mardi · des le mardi 1 septembre ».
      const morceaux =
        p.recurrence != null
          ? [libelleRecurrence(p.recurrence, p.date), `dès le ${jourEnToutesLettres(p.date)}`]
          : [jourEnToutesLettres(p.date)];
      if (p.time !== null) morceaux.push(p.time);
      if (p.location !== null) morceaux.push(p.location);
      return { titre: p.title, detail: morceaux.join(' · '), puces: [] };
    }
    case 'TASK': {
      const p = proposition.payload;
      return {
        titre: p.title,
        detail: p.dueDate === null ? 'Sans date' : `Pour le ${jourEnToutesLettres(p.dueDate)}`,
        puces: [],
      };
    }
    case 'REMINDER': {
      const p = proposition.payload;
      return { titre: p.message, detail: `${jourEnToutesLettres(p.date)} · ${p.time}`, puces: [] };
    }
    case 'CHECKLIST': {
      const p = proposition.payload;
      return { titre: p.title, detail: p.eventTitle, puces: p.items };
    }
    case 'FACT': {
      const p = proposition.payload;
      // « Concerne » plutot que « pour » : un fait n'est destine a personne, il PORTE sur
      // quelqu'un — et ce quelqu'un doit etre lisible avant de valider.
      return {
        titre: p.texte,
        detail: p.sujet === null ? 'Concerne tout le foyer' : `Concerne ${p.sujet}`,
        puces: [],
      };
    }
  }
}

interface Props {
  elementId: string;
  utilisateurId: string;
  version: number;
  retour: () => void;
  signaler: (message: string) => void;
  rafraichir: () => void;
}

/**
 * Combien de propositions par lot. Huit tient dans un ecran de telephone sans defiler
 * jusqu'a l'oubli, et laisse le pied de page toujours visible.
 */
const TAILLE_DU_LOT = 8;

export function Compris({ elementId, utilisateurId, version, retour, signaler, rafraichir }: Props) {
  // L'element est RECHARGE ici, et non recu en propriete : apres une reponse, Colette
  // pose souvent la question suivante, et un instantane fige ne la montrerait jamais.
  const etat = useDonnees(
    async (signal) => {
      const [boite, propositions, foyer] = await Promise.all([
        api.boite(signal),
        api.propositions(signal),
        // Les membres du foyer alimentent le choix du cercle (« Choisir… »).
        api.foyer(signal),
      ]);
      const element = boite.items.find((i) => i.id === elementId) ?? null;
      return {
        element,
        membres: foyer.household.members,
        propositions: propositions.proposals.filter(
          (p) => p.inboxItemId === elementId && p.status === 'PROPOSED',
        ),
      };
    },
    `${elementId}:${version}`,
  );

  const [occupe, setOccupe] = useState(false);
  /** Le lot affiche. Voir la derivation plus bas : il est BORNE a chaque rendu. */
  const [lot, setLot] = useState(0);
  // Le cercle choisi s'applique a TOUTES les validations de ce message : une dictee
  // est une seule histoire, on ne demande pas « pour qui ? » a chaque morceau.
  const [cercle, setCercle] = useState<Cercle>({ visibility: 'HOUSEHOLD', sharedWithUserIds: [] });

  // Quand la phrase disait « pour qui » (« surprise pour papa avec maman »), Colette a
  // deja resolu le cercle : on PREREMPLIT le selecteur, UNE fois — jamais par-dessus un
  // choix fait a la main.
  const prerempli = useRef<string | null>(null);
  const audienceSuggeree = etat.donnees?.element?.suggestedAudience ?? null;
  useEffect(() => {
    if (audienceSuggeree === null || prerempli.current === elementId) return;
    prerempli.current = elementId;
    setCercle({
      visibility: audienceSuggeree.visibility,
      sharedWithUserIds: audienceSuggeree.sharedWithUserIds ?? [],
    });
  }, [audienceSuggeree, elementId]);

  // Un cercle « Choisir… » sans personne cochee ne peut pas partir : parite avec le
  // schema partage, mais dit AVANT tout appel reseau.
  const cercleInvalide = cercle.visibility === 'CHOSEN' && cercle.sharedWithUserIds.length === 0;
  const cercleAEnvoyer = cercle.visibility === 'HOUSEHOLD' ? undefined : cercle;
  const garderLeCercle = (): boolean => {
    if (cercleInvalide) {
      signaler('Choisis au moins une personne, ou reviens à « Tout le foyer »');
      return false;
    }
    return true;
  };

  const agir = async (action: () => Promise<unknown>, message: string) => {
    if (occupe) return;
    setOccupe(true);
    try {
      const resultat = await action();
      signaler(message + suffixeGoogle(resultat));
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
      rafraichir();
    } finally {
      setOccupe(false);
    }
  };

  const element: ElementBoite | null = etat.donnees?.element ?? null;
  const propositions = etat.donnees?.propositions ?? [];

  /**
   * Valider par LOTS quand il y en a beaucoup (correctif du 2026-08-28).
   *
   * Une semaine complete dictee d'un coup produit une trentaine de propositions. Les
   * empiler toutes revenait a demander de lire quatre metres d'ecran avant de decider —
   * et « rien sans ton accord » suppose qu'on ait pu lire.
   *
   * En dessous du seuil, RIEN ne change : l'ecran ordinaire ne doit pas payer le prix
   * du cas rare.
   */
  const nombreDeLots = Math.ceil(propositions.length / TAILLE_DU_LOT);
  const parLots = propositions.length > TAILLE_DU_LOT;
  // Borne a chaque rendu : valider un lot RACCOURCIT la liste, et l'index pourrait
  // pointer au-dela de la fin.
  const lotCourant = parLots ? Math.min(lot, nombreDeLots - 1) : 0;
  const duLot = parLots
    ? propositions.slice(lotCourant * TAILLE_DU_LOT, (lotCourant + 1) * TAILLE_DU_LOT)
    : propositions;
  const aValider = parLots ? duLot : propositions;
  // Replis `?? []` volontaires malgre les types : ces valeurs viennent du reseau, donc
  // ne sont pas verifiees a la compilation. Un ecran blanc sur le parcours principal
  // coute infiniment plus cher que deux garde-fous.
  const precisions = element?.precisions ?? [];
  // Une seule question a la fois : trois d'affilee, ce n'est plus une conversation,
  // c'est un formulaire.
  const question: Question | undefined = element?.questions?.[0];
  // Une question en attente ne meurt JAMAIS en silence : si on valide sans repondre,
  // la confirmation le dit — le geste reste libre, l'information reste due.
  // Un fait n'apparait dans AUCUN planning (etape B2) : « ajouté à ton planning » serait
  // faux pour lui. Le message dit ce qui s'est vraiment passe, chose par chose.
  const messageValidation = (validees: Proposition[]) => {
    const faits = validees.filter((p) => p.kind === 'FACT').length;
    const autres = validees.length - faits;
    const planning =
      autres === 0
        ? null
        : autres === 1
          ? 'Ajouté à ton planning'
          : `${autres} éléments ajoutés à ton planning`;
    const base =
      planning === null
        ? 'Colette s’en souviendra'
        : faits === 0
          ? planning
          : `${planning}, et Colette s’en souviendra`;
    return question === undefined
      ? base
      : `${base} — Colette a encore une question, réponds quand tu veux depuis la Boîte`;
  };

  return (
    <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '22px 20px 12px 16px' }}>
        <button
          type="button"
          onClick={retour}
          aria-label="Retour"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
        >
          <ChevronGauche taille={24} couleur={C.encre} epaisseur={2} />
        </button>
        <div style={{ fontFamily: POLICE_TITRE, fontWeight: 600, fontSize: 20, color: C.encre }}>
          Voici ce que j'ai compris
        </div>
      </div>

      {etat.chargement && etat.donnees === null ? (
        <Chargement />
      ) : etat.erreur !== null && etat.donnees === null ? (
        <Panne message={etat.erreur} onReessayer={etat.recharger} />
      ) : element === null ? (
        <Panne message="Ce message est introuvable." onReessayer={retour} />
      ) : (
        <>
          <div
            style={{
              flex: '1 1 0',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '0 20px',
            }}
          >
            <div
              style={{
                background: C.page,
                borderRadius: 12,
                padding: '12px 14px',
                fontSize: 13,
                color: C.secondaire,
                lineHeight: 1.45,
                fontStyle: 'italic',
              }}
            >
              {element.type === 'PHOTO' && element.text === '' ? 'Ta photo' : `« ${element.text} »`}
            </div>

            {element.type === 'PHOTO' ? <PhotoDeBoite elementId={element.id} /> : null}

            {precisions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {precisions.map((precision, i) => (
                  <div
                    key={`${i}-${precision}`}
                    style={{
                      alignSelf: 'flex-end',
                      maxWidth: '85%',
                      background: C.violet,
                      color: '#FFFFFF',
                      borderRadius: '14px 14px 4px 14px',
                      padding: '8px 12px',
                      fontSize: 13.5,
                      fontWeight: 600,
                    }}
                  >
                    {precision}
                  </div>
                ))}
              </div>
            ) : null}

            {element.summary != null ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: C.violet,
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <div style={{ fontSize: 15, fontWeight: 700, color: C.encre, lineHeight: 1.4 }}>
                  {element.summary}
                </div>
              </div>
            ) : null}

            {/* Les incertitudes etaient stockees mais JAMAIS montrees : face au planning
                type de Marianne, Colette avait explique pourquoi elle ne creait rien —
                dans un champ invisible. Un ecran vide sans explication est un mensonge
                par omission. */}
            {(element.uncertainties ?? []).length > 0 ? (
              <div
                style={{
                  background: C.page,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div
                  style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: C.secondaire }}
                >
                  CE QUE COLETTE N'A PAS PU FAIRE, ET POURQUOI
                </div>
                {(element.uncertainties ?? []).map((incertitude) => (
                  <div key={incertitude} style={{ fontSize: 13.5, color: C.encre, lineHeight: 1.5 }}>
                    {incertitude}
                  </div>
                ))}
              </div>
            ) : null}

            {/* LE BON JOUR (2026-09-28) : quand Colette a corrige le jour d'un rendez-vous,
                elle le dit ICI, juste avant ce qu'elle propose. Sous son propre titre :
                ce n'est pas une chose qu'elle n'a pas pu faire, c'est une chose qu'elle a
                faite et qu'il faut relire. */}
            {(element.verifications ?? []).length > 0 ? (
              <div
                style={{
                  background: C.corailFond,
                  border: `1px solid ${C.corailBordure}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {/* Le titre s'ecrit a l'ENCRE, pas en corail : mesure sur le bloc, le corail
                    tombe a 2,6 dans le theme blanc et passe sous 4,5 dans les deux autres
                    (test/contraste.spec.ts). Le corail marque le bloc, il ne l'ecrit pas. */}
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: C.encre }}>
                  À VÉRIFIER AVANT DE VALIDER
                </div>
                {(element.verifications ?? []).map((remarque) => (
                  <div key={remarque} style={{ fontSize: 13.5, color: C.encre, lineHeight: 1.5 }}>
                    {remarque}
                  </div>
                ))}
              </div>
            ) : null}

            {question !== undefined ? (
              <BlocQuestion
                question={question}
                occupe={occupe}
                repondre={(reponse) =>
                  void agir(() => api.preciser(element.id, reponse), 'C’est noté')
                }
              />
            ) : null}

            {propositions.length > 0 ? (
              <div
                style={{
                  background: C.carte,
                  border: `1px solid ${C.bordure}`,
                  borderRadius: 14,
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: C.secondaire }}>
                  POUR QUI ?
                </div>
                <ChoixCercle
                  membres={etat.donnees?.membres ?? []}
                  utilisateurId={utilisateurId}
                  valeur={cercle}
                  onChange={setCercle}
                />
              </div>
            ) : null}

            {propositions.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '30px 10px',
                  textAlign: 'center',
                }}
              >
                <Coche taille={34} couleur={C.menthe} epaisseur={2.4} />
                {(element.uncertainties ?? []).length > 0 && element.status === 'PROCESSED' ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.encre }}>
                      Rien à créer pour l'instant
                    </div>
                    <div style={{ fontSize: 14, color: C.secondaire, lineHeight: 1.5 }}>
                      Colette t'explique pourquoi ci-dessus. Tu peux répondre à sa question ou
                      préciser ce que tu attends.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.encre }}>Tout est décidé</div>
                    <div style={{ fontSize: 14, color: C.secondaire }}>
                      Il n'y a plus rien à valider sur ce message.
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
                {parLots ? (
                  <EnteteDuLot
                    lot={lotCourant}
                    nombreDeLots={nombreDeLots}
                    affichees={duLot.length}
                    restantes={propositions.length}
                    onSuivant={() => setLot((n) => n + 1)}
                    onPrecedent={() => setLot((n) => n - 1)}
                  />
                ) : null}
                {duLot.map((proposition) => (
                  <CarteProposition
                    key={proposition.id}
                    proposition={proposition}
                    occupe={occupe}
                    onValider={() => {
                      if (!garderLeCercle()) return;
                      void agir(
                        () => api.valider(proposition.id, cercleAEnvoyer),
                        messageValidation([proposition]),
                      );
                    }}
                    onCorriger={(payload) => {
                      if (!garderLeCercle()) return;
                      // Corriger APPLIQUE aussi : meme chemin que valider, donc meme
                      // message et meme honnetete si Google n'a pas repondu.
                      void agir(
                        () => api.corriger(proposition.id, payload, cercleAEnvoyer),
                        messageValidation([proposition]),
                      );
                    }}
                    onRefuser={() => void agir(() => api.refuser(proposition.id), 'Proposition écartée')}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '14px 20px 22px 20px',
              borderTop: `1px solid ${C.bordure}`,
            }}
          >
            {propositions.length > 0 ? (
              <BoutonPrincipal
                actif={!occupe}
                onClick={() => {
                  if (!garderLeCercle()) return;
                  void agir(async () => {
                    // En serie et non en parallele : chaque validation ecrit dans le
                    // planning et referme l'element quand la derniere est decidee.
                    // Les lancer ensemble ferait courir plusieurs transactions sur la
                    // meme cloture.
                    const resultats = [];
                    for (const proposition of aValider) {
                      resultats.push(await api.valider(proposition.id, cercleAEnvoyer));
                    }
                    return resultats;
                  }, messageValidation(aValider));
                }}
              >
                {occupe
                  ? 'Validation…'
                  : parLots
                    ? `Valider ce lot (${duLot.length})`
                    : 'Tout valider'}
              </BoutonPrincipal>
            ) : null}
            <button
              type="button"
              onClick={retour}
              style={{
                background: 'none',
                border: 'none',
                textAlign: 'center',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                color: C.secondaire,
                cursor: 'pointer',
              }}
            >
              {propositions.length > 0 ? 'Voir plus tard' : 'Retour à la boîte'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * La question de Colette.
 *
 * Les suggestions passent avant le champ libre : sur un telephone, en marchant, choisir
 * entre trois reponses est la seule interaction realiste. Le champ reste la pour tout ce
 * que Colette n'avait pas prevu.
 */
function BlocQuestion({
  question,
  occupe,
  repondre,
}: {
  question: Question;
  occupe: boolean;
  repondre: (reponse: string) => void;
}) {
  const [libre, setLibre] = useState('');

  return (
    <div
      style={{
        background: C.violetFond,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: C.violetTexte }}>
        COLETTE A BESOIN DE SAVOIR
      </div>
      <div style={{ fontSize: 15.5, fontWeight: 700, color: C.encre, lineHeight: 1.35 }}>
        {question.question}
      </div>

      {question.suggestions.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {question.suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              disabled={occupe}
              onClick={() => repondre(suggestion)}
              style={{
                background: C.carte,
                border: `1px solid ${C.violetBordure}`,
                borderRadius: 999,
                padding: '8px 14px',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'inherit',
                color: C.violetTexte,
                cursor: occupe ? 'default' : 'pointer',
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const reponse = libre.trim();
          if (reponse === '' || occupe) return;
          setLibre('');
          repondre(reponse);
        }}
        style={{ display: 'flex', gap: 8 }}
      >
        <input
          value={libre}
          onChange={(e) => setLibre(e.target.value)}
          placeholder="Ou réponds ici…"
          aria-label={question.question}
          style={{
            flex: 1,
            minWidth: 0,
            height: 42,
            border: `1px solid ${C.violetBordure}`,
            borderRadius: 11,
            padding: '0 12px',
            // 16 px au minimum : en dessous, iOS zoome tout seul au focus.
            fontSize: 16,
            fontFamily: POLICE_CORPS,
            color: C.encre,
            background: C.carte,
            outline: 'none',
          }}
        />
        {/* Pendant l'envoi, le bouton ne se GRISE plus : il passe en menthe et respire,
            la couleur de ce qui est accepte partout ailleurs. Se griser disait « c'est
            parti » sans dire « c'est arrive » — un bouton eteint ressemble a une panne. */}
        <button
          type="submit"
          disabled={occupe || libre.trim() === ''}
          className={occupe ? 'pulsation-menthe' : undefined}
          style={{
            height: 42,
            padding: '0 16px',
            border: 'none',
            borderRadius: 11,
            background: occupe
              ? MENTHE_PLEINE
              : libre.trim() === ''
                ? C.inactif
                : DEGRADE_ACTION,
            color: occupe ? TRAIT_COCHE : '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: libre.trim() === '' || occupe ? 'default' : 'pointer',
            transition: 'background 0.25s ease-out, color 0.25s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {occupe ? (
            <>
              <Coche taille={15} couleur={TRAIT_COCHE} epaisseur={2.8} />
              Reçu
            </>
          ) : (
            'Envoyer'
          )}
        </button>
      </form>
    </div>
  );
}

function CarteProposition({
  proposition,
  occupe,
  onValider,
  onCorriger,
  onRefuser,
}: {
  proposition: Proposition;
  occupe: boolean;
  onValider: () => void;
  onCorriger: (payload: PayloadCorrige) => void;
  onRefuser: () => void;
}) {
  const { titre, detail, puces } = decrire(proposition);
  const [correction, setCorrection] = useState(false);

  /**
   * Les AUTRES plats proposes pour la meme case (2026-08-29).
   *
   * Choisir coute moins que d'inventer, et bien moins que de refuser : sans ces
   * boutons, trois plats proposes obligeraient a en refuser deux — quatorze refus pour
   * une semaine, exactement la charge qu'on essaie de retirer.
   *
   * Le choix passe par le chemin de CORRECTION, qui applique deja : aucune route
   * nouvelle, et le geste reste « un seul appui ».
   */
  const alternatives =
    proposition.kind === 'EVENT' ? (proposition.payload.alternatives ?? []) : [];

  // En correction, la carte devient un formulaire : le contenu n'est plus du texte mort.
  if (correction) {
    return (
      <div
        style={{
          background: C.carte,
          border: `1px solid ${C.violetBordure}`,
          borderRadius: 14,
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            alignSelf: 'flex-start',
            background: C.violetFond,
            color: C.violetTexte,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.6,
            padding: '3px 9px',
            borderRadius: 999,
          }}
        >
          {ETIQUETTES[proposition.kind]}
        </div>
        <FormulaireCorrection
          proposition={proposition}
          occupe={occupe}
          onEnregistrer={(payload) => onCorriger(payload)}
          onAnnuler={() => setCorrection(false)}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        opacity: occupe ? 0.6 : 1,
      }}
    >
      <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            alignSelf: 'flex-start',
            background: C.violetFond,
            color: C.violetTexte,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.6,
            padding: '3px 9px',
            borderRadius: 999,
          }}
        >
          {ETIQUETTES[proposition.kind]}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.encre }}>{titre}</div>
        {detail !== null ? <div style={{ fontSize: 13, color: C.secondaire }}>{detail}</div> : null}
        {puces.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {puces.map((puce) => (
              <div
                key={puce}
                style={{
                  border: `1px solid ${C.bordure}`,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 13,
                  color: C.encre,
                }}
              >
                {puce}
              </div>
            ))}
          </div>
        ) : null}
        {alternatives.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {alternatives.map((autre) => (
              <button
                key={autre}
                type="button"
                disabled={occupe}
                className="pressable"
                aria-label={`Choisir plutôt : ${autre}`}
                onClick={() =>
                  onCorriger({
                    ...(proposition.payload as unknown as PayloadCorrige),
                    title: autre,
                  })
                }
                style={{
                  border: `1px solid ${C.violetBordure}`,
                  background: C.violetFond,
                  color: C.violetTexte,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: occupe ? 'default' : 'pointer',
                }}
              >
                {autre}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          disabled={occupe}
          aria-label={`Valider : ${titre}`}
          onClick={onValider}
          className="pressable"
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            // La menthe, et non le voile ordinaire : c'est le geste affirmatif de
            // l'ecran, il ne doit pas se confondre avec « Corriger » et « Refuser ».
            background: C.mentheFond,
            border: `1px solid ${C.mentheBordure}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: occupe ? 'default' : 'pointer',
          }}
        >
          <Coche taille={20} couleur={C.menthe} epaisseur={2.4} />
        </button>
        <button
          type="button"
          disabled={occupe}
          aria-label={`Corriger : ${titre}`}
          onClick={() => setCorrection(true)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 12,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: C.violetTexte,
            cursor: occupe ? 'default' : 'pointer',
          }}
        >
          Corriger
        </button>
        <BoutonDeuxGestes
          libelle="Refuser"
          libelleArme="Confirmer ?"
          aria={`Refuser : ${titre}`}
          ariaArme={`Confirmer le refus : ${titre}`}
          onConfirmer={onRefuser}
          occupe={occupe}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'inherit',
            color: C.tertiaire,
            cursor: occupe ? 'default' : 'pointer',
          }}
          styleArme={{ color: C.corailTexte, fontWeight: 800 }}
        />
      </div>
    </div>
  );
}

/**
 * L'en-tete d'un lot : ou on en est, et comment avancer sans rien decider.
 *
 * « Voir le lot suivant » est essentiel : une proposition sur laquelle on hesite ne doit
 * jamais bloquer les vingt-neuf autres.
 */
function EnteteDuLot({
  lot,
  nombreDeLots,
  affichees,
  restantes,
  onSuivant,
  onPrecedent,
}: {
  lot: number;
  nombreDeLots: number;
  affichees: number;
  restantes: number;
  onSuivant: () => void;
  onPrecedent: () => void;
}) {
  const style = {
    border: `1px solid ${C.bordureForte}`,
    background: 'none',
    color: C.encre,
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 800,
    fontFamily: 'inherit',
    cursor: 'pointer',
  } as const;

  return (
    <div
      style={{
        background: C.carteDouce,
        border: `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.encre }}>
          Lot {lot + 1} sur {nombreDeLots}
        </div>
        <div style={{ fontSize: 12, color: C.secondaire }}>
          {affichees} proposition{affichees > 1 ? 's' : ''} affichée{affichees > 1 ? 's' : ''} sur{' '}
          {restantes} qui attendent.
        </div>
      </div>
      {lot > 0 ? (
        <button type="button" className="pressable" onClick={onPrecedent} style={style}>
          Précédent
        </button>
      ) : null}
      {lot < nombreDeLots - 1 ? (
        <button
          type="button"
          className="pressable"
          onClick={onSuivant}
          aria-label="Voir le lot suivant"
          style={style}
        >
          Suivant
        </button>
      ) : null}
    </div>
  );
}
